/**
 * Integration Marketplace Demo — the catalog of connectable systems.
 *
 * THROWAWAY DEMO CODE (see ./README.md). Static, offline definitions for every
 * supported integration: branding, auth fields, and the raw tables the scanner
 * "discovers" (each with an auto-detected schema + sample rows). All data here
 * is fabricated for the demo and maps to no real backend.
 */

import {
  CANONICAL_PRODUCTS,
  CORE_PRODUCTS,
  withDiscrepancies,
  type CanonicalProduct,
} from './catalog-products';
import type {
  AuthField,
  DetectedLink,
  FieldType,
  GentlyEntity,
  IntegrationDef,
  ResolvedSourceProduct,
  SchemaField,
  SourceRow,
} from './types';

/** Products a connected (non-baseline) source exposes: full set, with the
 *  demo's per-source price/vendor discrepancies applied. */
const CONNECTED_PRODUCTS = withDiscrepancies(CANONICAL_PRODUCTS);

// ---------------------------------------------------------------------------
// Products link builder — source-authentic column naming over the canonical set
// ---------------------------------------------------------------------------

interface ProductColumn {
  col: string;
  type: FieldType;
  get: (p: CanonicalProduct, index: number) => string | number | boolean | null;
  mapsTo?: string;
  pk?: boolean;
  pii?: boolean;
  nullable?: boolean;
}

function buildProductsLink(args: {
  id: string;
  sourceName: string;
  description: string;
  rowCount: number;
  columns: ProductColumn[];
  /** Which product set this source exposes (defaults to the connected set). */
  products?: CanonicalProduct[];
}): DetectedLink {
  const products = args.products ?? CONNECTED_PRODUCTS;
  const schema: SchemaField[] = args.columns.map((c) => ({
    name: c.col,
    type: c.type,
    nullable: c.nullable ?? false,
    sample: String(c.get(products[0], 0)),
    mapsTo: c.mapsTo,
    primaryKey: c.pk,
    pii: c.pii,
  }));
  const sampleRows: SourceRow[] = products.map((p, i) => {
    const row: SourceRow = {};
    for (const c of args.columns) row[c.col] = c.get(p, i);
    return row;
  });
  const resolved: ResolvedSourceProduct[] = products.map((p) => ({
    sku: p.sku,
    name: p.name,
    description: p.description,
    category: p.category,
    manufacturer: p.manufacturer,
    uom: p.uom,
    listPrice: p.listPrice,
    cost: p.cost,
    onHand: p.onHand,
  }));
  return {
    id: args.id,
    sourceName: args.sourceName,
    displayName: 'Products',
    description: args.description,
    entity: 'products',
    rowCount: args.rowCount,
    schema,
    sampleRows,
    products: resolved,
    recommended: true,
  };
}

// Source-specific products column sets ------------------------------------------------

const SAP_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'MATNR',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  { col: 'MAKTX', type: 'string', get: (p) => p.name, mapsTo: 'product.name' },
  { col: 'MTART', type: 'string', get: () => 'HAWA', mapsTo: 'product.type' },
  {
    col: 'MATKL',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'MEINS',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'NTGEW',
    type: 'decimal',
    get: (p) => p.weightLbs,
    mapsTo: 'product.weight',
  },
  {
    col: 'STPRS',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'LABST',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'BISMT',
    type: 'string',
    get: (p) => p.legacyPart,
    mapsTo: 'product.legacyRef',
    nullable: true,
  },
  { col: 'ERSDA', type: 'date', get: (p) => p.createdOn },
];

const ORACLE_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'ITEM_NUMBER',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'DESCRIPTION',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'LONG_DESCRIPTION',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'ITEM_CLASS',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'PRIMARY_UOM_CODE',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'LIST_PRICE',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'UNIT_COST',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'ON_HAND_QTY',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'MANUFACTURER',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  { col: 'INVENTORY_ITEM_ID', type: 'integer', get: (_p, i) => 4500100 + i },
];

const NETSUITE_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'itemid',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'displayname',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'salesdescription',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'custitem_category',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'baseunit',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'baseprice',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'cost',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'quantityonhand',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'manufacturer',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  {
    col: 'upccode',
    type: 'string',
    get: (p) => p.upc,
    mapsTo: 'product.upc',
    nullable: true,
  },
  { col: 'internalid', type: 'integer', get: (_p, i) => 1820 + i, pk: false },
];

const DYNAMICS_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'ItemId',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'ProductName',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'SearchName',
    type: 'string',
    get: (p) => p.legacyPart,
    mapsTo: 'product.legacyRef',
    nullable: true,
  },
  {
    col: 'ItemGroupId',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'UnitId',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'SalesPrice',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'CostPrice',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'PhysicalInvent',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'PrimaryVendorName',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
];

const AS400_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'GIITEM',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'GIDESCR',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'GIDEPT',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'GIUOM',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'GISELPRC',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'GISTDCST',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'GIONHAND',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'GIVNDR',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  {
    col: 'GIWGHT',
    type: 'decimal',
    get: (p) => p.weightLbs,
    mapsTo: 'product.weight',
    nullable: true,
  },
];

// Clean, analytics-warehouse style (Snowflake / Databricks / Fabric / Datasphere)
const WAREHOUSE_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'PRODUCT_SKU',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'PRODUCT_NAME',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'PRODUCT_DESC',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'CATEGORY',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'MANUFACTURER',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  {
    col: 'UOM',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'LIST_PRICE',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'STD_COST',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'QTY_ON_HAND',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  { col: 'LAST_UPDATED', type: 'datetime', get: () => '2026-06-21T04:15:00Z' },
];

const GENERIC_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'prod_code',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'prod_desc',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'prod_class',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'unit',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'price',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'cost',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'qty_avail',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'vendor',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
];

// Oracle E-Business Suite — MTL_SYSTEM_ITEMS_B
const EBS_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'SEGMENT1',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'DESCRIPTION',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'PRIMARY_UOM_CODE',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'LIST_PRICE_PER_UNIT',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'COST',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'ITEM_CATALOG_GROUP',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'MANUFACTURER',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  {
    col: 'ON_HAND',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  { col: 'INVENTORY_ITEM_ID', type: 'integer', get: (_p, i) => 200100 + i },
];

// Oracle PeopleSoft FSCM — PS_MASTER_ITEM_TBL
const PEOPLESOFT_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'INV_ITEM_ID',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  { col: 'DESCR', type: 'string', get: (p) => p.name, mapsTo: 'product.name' },
  {
    col: 'DESCR254_MIXED',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'UNIT_MEASURE_STD',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'STD_PRICE',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'AVERAGE_COST',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'CATEGORY_CD',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'MFG_ID',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  { col: 'SETID', type: 'string', get: () => 'SHARE' },
];

// Infor M3 / CloudSuite — MITMAS
const INFOR_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'ITNO',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  { col: 'ITDS', type: 'string', get: (p) => p.name, mapsTo: 'product.name' },
  {
    col: 'FUDS',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'ITGR',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'UNMS',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'SAPR',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'STCS',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'SUNO',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  { col: 'STAT', type: 'string', get: () => '20' },
];

// Epicor Kinetic — Erp.Part
const EPICOR_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'PartNum',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'PartDescription',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'ClassID',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'IUM',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'UnitPrice',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'StdUnitCost',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'OnHandQty',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'Manufacturer',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
];

// SYSPRO — InvMaster
const SYSPRO_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'StockCode',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'Description',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'LongDesc',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'ProductClass',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'UnitOfMeasure',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'SellingPrice',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'UnitCost',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'QtyOnHand',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'PreferredSupplier',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
];

// Workday Financials — Procurement Purchase Item (RaaS)
const WORKDAY_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'Purchase_Item_ID',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'Item_Name',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'Item_Description',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'Spend_Category',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'Unit_of_Measure',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'Standard_Price',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'Supplier_Reference',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  {
    col: 'WID',
    type: 'string',
    get: (_p, i) => `0a1b2c${(30 + i).toString(16)}d4e5`,
  },
];

// Microsoft Dynamics NAV (Navision) — Item card via OData web services
const NAV_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'No',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  {
    col: 'Description',
    type: 'string',
    get: (p) => p.name,
    mapsTo: 'product.name',
  },
  {
    col: 'Description_2',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'Item_Category_Code',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'Base_Unit_of_Measure',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'Unit_Price',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'Unit_Cost',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'Inventory',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'Vendor_No',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
];

// Oracle JD Edwards EnterpriseOne — F4101 Item Master (joined with F4105/F41021)
const JDE_PRODUCT_COLUMNS: ProductColumn[] = [
  {
    col: 'IMLITM',
    type: 'string',
    get: (p) => p.sku,
    mapsTo: 'product.sku',
    pk: true,
  },
  { col: 'IMDSC1', type: 'string', get: (p) => p.name, mapsTo: 'product.name' },
  {
    col: 'IMDSC2',
    type: 'string',
    get: (p) => p.description,
    mapsTo: 'product.description',
    nullable: true,
  },
  {
    col: 'IMSRP1',
    type: 'string',
    get: (p) => p.category,
    mapsTo: 'product.category',
  },
  {
    col: 'IMUOM1',
    type: 'string',
    get: (p) => p.uom,
    mapsTo: 'product.unitOfMeasure',
  },
  {
    col: 'UPMPR',
    type: 'decimal',
    get: (p) => p.listPrice,
    mapsTo: 'product.listPrice',
  },
  {
    col: 'COUNCS',
    type: 'decimal',
    get: (p) => p.cost,
    mapsTo: 'product.standardCost',
  },
  {
    col: 'LIPQOH',
    type: 'integer',
    get: (p) => p.onHand,
    mapsTo: 'inventory.onHand',
  },
  {
    col: 'IBVEND',
    type: 'string',
    get: (p) => p.manufacturer,
    mapsTo: 'product.manufacturer',
  },
  { col: 'IMITM', type: 'integer', get: (_p, i) => 60001 + i },
];

// ---------------------------------------------------------------------------
// Secondary links (purchase orders / suppliers / inventory / rebates / SPA)
// Lighter than products but believable: real-ish schema + a few sample rows.
// ---------------------------------------------------------------------------

function field(
  name: string,
  type: FieldType,
  sample: string,
  extra?: Partial<SchemaField>,
): SchemaField {
  return { name, type, nullable: false, sample, ...extra };
}

function purchaseOrdersLink(id: string, sourceName: string): DetectedLink {
  const schema: SchemaField[] = [
    field('PO_NUMBER', 'string', 'PO-2026-04417', {
      mapsTo: 'purchaseOrder.number',
      primaryKey: true,
    }),
    field('VENDOR_ID', 'string', 'V-100482', {
      mapsTo: 'purchaseOrder.supplierId',
    }),
    field('ORDER_DATE', 'date', '2026-06-18', {
      mapsTo: 'purchaseOrder.orderedAt',
    }),
    field('STATUS', 'string', 'OPEN', { mapsTo: 'purchaseOrder.status' }),
    field('LINE_ITEM', 'string', 'QO120', { mapsTo: 'purchaseOrderLine.sku' }),
    field('QTY_ORDERED', 'integer', '500', {
      mapsTo: 'purchaseOrderLine.quantity',
    }),
    field('UNIT_PRICE', 'decimal', '7.10', {
      mapsTo: 'purchaseOrderLine.unitPrice',
    }),
    field('CURRENCY', 'string', 'USD', { mapsTo: 'purchaseOrder.currency' }),
  ];
  const sampleRows: SourceRow[] = [
    {
      PO_NUMBER: 'PO-2026-04417',
      VENDOR_ID: 'V-100482',
      ORDER_DATE: '2026-06-18',
      STATUS: 'OPEN',
      LINE_ITEM: 'QO120',
      QTY_ORDERED: 500,
      UNIT_PRICE: 7.1,
      CURRENCY: 'USD',
    },
    {
      PO_NUMBER: 'PO-2026-04418',
      VENDOR_ID: 'V-100371',
      ORDER_DATE: '2026-06-18',
      STATUS: 'OPEN',
      LINE_ITEM: 'THHN-12-BLK',
      QTY_ORDERED: 120,
      UNIT_PRICE: 49.9,
      CURRENCY: 'USD',
    },
    {
      PO_NUMBER: 'PO-2026-04392',
      VENDOR_ID: 'V-100482',
      ORDER_DATE: '2026-06-15',
      STATUS: 'RECEIVED',
      LINE_ITEM: 'EMT-1/2-10',
      QTY_ORDERED: 1000,
      UNIT_PRICE: 3.85,
      CURRENCY: 'USD',
    },
    {
      PO_NUMBER: 'PO-2026-04355',
      VENDOR_ID: 'V-100905',
      ORDER_DATE: '2026-06-11',
      STATUS: 'PARTIAL',
      LINE_ITEM: 'LED-HB-150W',
      QTY_ORDERED: 64,
      UNIT_PRICE: 96.4,
      CURRENCY: 'USD',
    },
  ];
  return {
    id,
    sourceName,
    displayName: 'Purchase Orders',
    description: 'Open and historical purchase orders with line-level detail.',
    entity: 'purchase-orders',
    rowCount: 1843206,
    schema,
    sampleRows,
    recommended: true,
  };
}

function suppliersLink(id: string, sourceName: string): DetectedLink {
  const schema: SchemaField[] = [
    field('VENDOR_ID', 'string', 'V-100482', {
      mapsTo: 'supplier.externalId',
      primaryKey: true,
    }),
    field('VENDOR_NAME', 'string', 'Square D / Schneider Electric', {
      mapsTo: 'supplier.name',
    }),
    field('CONTACT_EMAIL', 'string', 'orders@se.com', {
      mapsTo: 'supplier.email',
      pii: true,
      nullable: true,
    }),
    field('PHONE', 'string', '+1-919-555-0142', {
      mapsTo: 'supplier.phone',
      pii: true,
      nullable: true,
    }),
    field('PAYMENT_TERMS', 'string', 'NET30', {
      mapsTo: 'supplier.paymentTerms',
    }),
    field('COUNTRY', 'string', 'US', { mapsTo: 'supplier.country' }),
    field('ACTIVE', 'boolean', 'true', { mapsTo: 'supplier.isActive' }),
  ];
  const sampleRows: SourceRow[] = [
    {
      VENDOR_ID: 'V-100482',
      VENDOR_NAME: 'Square D / Schneider Electric',
      CONTACT_EMAIL: 'orders@se.com',
      PHONE: '+1-919-555-0142',
      PAYMENT_TERMS: 'NET30',
      COUNTRY: 'US',
      ACTIVE: true,
    },
    {
      VENDOR_ID: 'V-100371',
      VENDOR_NAME: 'Southwire Company',
      CONTACT_EMAIL: 'edi@southwire.com',
      PHONE: '+1-770-555-0190',
      PAYMENT_TERMS: 'NET45',
      COUNTRY: 'US',
      ACTIVE: true,
    },
    {
      VENDOR_ID: 'V-100905',
      VENDOR_NAME: 'Acuity Brands (Lithonia)',
      CONTACT_EMAIL: 'csr@acuitybrands.com',
      PHONE: '+1-770-555-0177',
      PAYMENT_TERMS: 'NET30',
      COUNTRY: 'US',
      ACTIVE: true,
    },
  ];
  return {
    id,
    sourceName,
    displayName: 'Suppliers',
    description: 'Vendor master with contacts, terms, and remittance details.',
    entity: 'suppliers',
    rowCount: 128417,
    schema,
    sampleRows,
    recommended: false,
  };
}

function inventoryLink(id: string, sourceName: string): DetectedLink {
  const schema: SchemaField[] = [
    field('ITEM', 'string', 'QO120', {
      mapsTo: 'inventory.sku',
      primaryKey: true,
    }),
    field('WAREHOUSE', 'string', 'DC-ATL', {
      mapsTo: 'inventory.warehouse',
      primaryKey: true,
    }),
    field('ON_HAND', 'integer', '1240', { mapsTo: 'inventory.onHand' }),
    field('ALLOCATED', 'integer', '180', { mapsTo: 'inventory.allocated' }),
    field('ON_ORDER', 'integer', '500', { mapsTo: 'inventory.onOrder' }),
    field('BIN', 'string', 'A-12-03', {
      mapsTo: 'inventory.binLocation',
      nullable: true,
    }),
    field('SNAPSHOT_TS', 'datetime', '2026-06-22T06:00:00Z'),
  ];
  const sampleRows: SourceRow[] = [
    {
      ITEM: 'QO120',
      WAREHOUSE: 'DC-ATL',
      ON_HAND: 1240,
      ALLOCATED: 180,
      ON_ORDER: 500,
      BIN: 'A-12-03',
      SNAPSHOT_TS: '2026-06-22T06:00:00Z',
    },
    {
      ITEM: 'THHN-12-BLK',
      WAREHOUSE: 'DC-ATL',
      ON_HAND: 410,
      ALLOCATED: 36,
      ON_ORDER: 120,
      BIN: 'C-04-11',
      SNAPSHOT_TS: '2026-06-22T06:00:00Z',
    },
    {
      ITEM: 'EMT-1/2-10',
      WAREHOUSE: 'DC-DAL',
      ON_HAND: 880,
      ALLOCATED: 92,
      ON_ORDER: 0,
      BIN: 'B-22-07',
      SNAPSHOT_TS: '2026-06-22T06:00:00Z',
    },
  ];
  return {
    id,
    sourceName,
    displayName: 'Inventory Levels',
    description: 'Per-warehouse on-hand, allocated, and on-order snapshots.',
    entity: 'inventory',
    rowCount: 9621043,
    schema,
    sampleRows,
    recommended: false,
  };
}

function rebatesLink(id: string, sourceName: string): DetectedLink {
  const schema: SchemaField[] = [
    field('AGREEMENT_ID', 'string', 'RB-SE-2026', {
      mapsTo: 'rebateAgreement.externalId',
      primaryKey: true,
    }),
    field('SUPPLIER', 'string', 'Schneider Electric', {
      mapsTo: 'rebateAgreement.supplier',
    }),
    field('PROGRAM', 'string', 'Volume Growth Tier', {
      mapsTo: 'rebateAgreement.program',
    }),
    field('RATE_PCT', 'decimal', '3.50', { mapsTo: 'rebateAgreement.ratePct' }),
    field('PERIOD_START', 'date', '2026-01-01', {
      mapsTo: 'rebateAgreement.periodStart',
    }),
    field('PERIOD_END', 'date', '2026-12-31', {
      mapsTo: 'rebateAgreement.periodEnd',
    }),
    field('ACCRUED_AMOUNT', 'decimal', '184502.18', {
      mapsTo: 'rebateAgreement.accrued',
    }),
  ];
  const sampleRows: SourceRow[] = [
    {
      AGREEMENT_ID: 'RB-SE-2026',
      SUPPLIER: 'Schneider Electric',
      PROGRAM: 'Volume Growth Tier',
      RATE_PCT: 3.5,
      PERIOD_START: '2026-01-01',
      PERIOD_END: '2026-12-31',
      ACCRUED_AMOUNT: 184502.18,
    },
    {
      AGREEMENT_ID: 'RB-SW-2026',
      SUPPLIER: 'Southwire',
      PROGRAM: 'Annual Flat',
      RATE_PCT: 2.0,
      PERIOD_START: '2026-01-01',
      PERIOD_END: '2026-12-31',
      ACCRUED_AMOUNT: 97310.4,
    },
  ];
  return {
    id,
    sourceName,
    displayName: 'Rebate Agreements',
    description: 'Supplier rebate programs, tiers, and accrued amounts.',
    entity: 'rebates',
    rowCount: 31285,
    schema,
    sampleRows,
    recommended: false,
  };
}

function spaAgreementsLink(id: string, sourceName: string): DetectedLink {
  const schema: SchemaField[] = [
    field('SPA_NUMBER', 'string', 'SPA-44218', {
      mapsTo: 'spaAgreement.number',
      primaryKey: true,
    }),
    field('SUPPLIER', 'string', 'Schneider Electric', {
      mapsTo: 'spaAgreement.supplier',
    }),
    field('CUSTOMER', 'string', 'Turner Construction', {
      mapsTo: 'spaAgreement.customer',
    }),
    field('ITEM', 'string', 'QO120', { mapsTo: 'spaAgreement.sku' }),
    field('CONTRACT_COST', 'decimal', '5.95', {
      mapsTo: 'spaAgreement.contractCost',
    }),
    field('EFFECTIVE', 'date', '2026-03-01', {
      mapsTo: 'spaAgreement.effectiveDate',
    }),
    field('EXPIRES', 'date', '2026-12-31', {
      mapsTo: 'spaAgreement.expiresDate',
    }),
  ];
  const sampleRows: SourceRow[] = [
    {
      SPA_NUMBER: 'SPA-44218',
      SUPPLIER: 'Schneider Electric',
      CUSTOMER: 'Turner Construction',
      ITEM: 'QO120',
      CONTRACT_COST: 5.95,
      EFFECTIVE: '2026-03-01',
      EXPIRES: '2026-12-31',
    },
    {
      SPA_NUMBER: 'SPA-44102',
      SUPPLIER: 'Southwire',
      CUSTOMER: 'Holder Construction',
      ITEM: 'THHN-12-BLK',
      CONTRACT_COST: 44.2,
      EFFECTIVE: '2026-02-15',
      EXPIRES: '2026-12-31',
    },
  ];
  return {
    id,
    sourceName,
    displayName: 'SPA Agreements',
    description:
      'Ship-and-debit / special pricing agreements by customer and item.',
    entity: 'spa-agreements',
    rowCount: 2048517,
    schema,
    sampleRows,
    recommended: false,
  };
}

// ---------------------------------------------------------------------------
// Auth field presets
// ---------------------------------------------------------------------------

const OAUTH_FIELDS = (
  hostLabel: string,
  hostPlaceholder: string,
  hostDemo: string,
): AuthField[] => [
  {
    key: 'host',
    label: hostLabel,
    type: 'url',
    placeholder: hostPlaceholder,
    required: true,
    demoValue: hostDemo,
  },
  {
    key: 'clientId',
    label: 'Client ID',
    type: 'text',
    placeholder: 'e.g. 8f2c1a9e-...',
    required: true,
    demoValue: 'svc-gently-ingest',
  },
  {
    key: 'clientSecret',
    label: 'Client Secret',
    type: 'password',
    placeholder: '••••••••••••••••',
    required: true,
    demoValue: 'sk_live_8Hd92kPq1MvTnZ04',
  },
  {
    key: 'environment',
    label: 'Environment',
    type: 'select',
    options: ['Production', 'Sandbox', 'Pre-prod'],
    required: true,
    demoValue: 'Production',
  },
];

// ---------------------------------------------------------------------------
// The catalog
// ---------------------------------------------------------------------------

function commonLinks(
  prefix: string,
  names: Partial<Record<GentlyEntity, string>>,
): DetectedLink[] {
  const links: DetectedLink[] = [];
  if (names['purchase-orders'])
    links.push(purchaseOrdersLink(`${prefix}-po`, names['purchase-orders']));
  if (names['suppliers'])
    links.push(suppliersLink(`${prefix}-sup`, names['suppliers']));
  if (names['inventory'])
    links.push(inventoryLink(`${prefix}-inv`, names['inventory']));
  if (names['rebates'])
    links.push(rebatesLink(`${prefix}-reb`, names['rebates']));
  if (names['spa-agreements'])
    links.push(spaAgreementsLink(`${prefix}-spa`, names['spa-agreements']));
  return links;
}

export const INTEGRATIONS: IntegrationDef[] = [
  // ---- ERP systems ----
  {
    id: 'sap-s4hana',
    name: 'SAP S/4HANA Cloud',
    category: 'erp',
    tagline: 'Enterprise ERP',
    description:
      'Connect your SAP S/4HANA Cloud tenant to sync materials, procurement, and finance master data.',
    brandColor: '#0AA1DD',
    monogram: 'SAP',
    authMethod: 'oauth2',
    popular: true,
    authFields: OAUTH_FIELDS(
      'Tenant Host',
      'my300000.s4hana.cloud.sap',
      'my419872.s4hana.cloud.sap',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'sap-products',
        sourceName: 'MARA — Material Master',
        description:
          'Material master records joined with MAKT short texts and MBEW valuation.',
        rowCount: 4821337,
        columns: SAP_PRODUCT_COLUMNS,
      }),
      ...commonLinks('sap', {
        'purchase-orders': 'EKKO / EKPO — Purchasing Documents',
        suppliers: 'LFA1 — Vendor Master',
        inventory: 'MARD — Storage Location Stock',
        rebates: 'KONA — Rebate Agreements',
        'spa-agreements': 'A017 — Pricing Conditions',
      }),
    ],
  },
  {
    id: 'oracle-fusion',
    name: 'Oracle Fusion Cloud',
    category: 'erp',
    tagline: 'Cloud ERP & SCM',
    description:
      'Sync items, suppliers, and procurement from Oracle Fusion Cloud SCM & Procurement.',
    brandColor: '#C74634',
    monogram: 'ORA',
    authMethod: 'oauth2',
    popular: true,
    authFields: OAUTH_FIELDS(
      'Fusion Host',
      'https://yourpod.fa.us2.oraclecloud.com',
      'https://eevn.fa.us6.oraclecloud.com',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'ora-products',
        sourceName: 'EGP_SYSTEM_ITEMS_B — Item Master',
        description:
          'Product Information Management item master with cost and on-hand.',
        rowCount: 5187742,
        columns: ORACLE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('ora', {
        'purchase-orders': 'PO_HEADERS_ALL — Purchase Orders',
        suppliers: 'POZ_SUPPLIERS — Supplier Master',
        inventory: 'INV_ONHAND_QUANTITIES — On-Hand',
        rebates: 'OZF_OFFERS — Trade Offers',
        'spa-agreements': 'QP_LIST_HEADERS — Price Lists',
      }),
    ],
  },
  {
    id: 'netsuite',
    name: 'Oracle NetSuite',
    category: 'erp',
    tagline: 'Cloud ERP',
    description:
      'Connect NetSuite to sync items, inventory, vendors, and purchase orders via SuiteTalk.',
    brandColor: '#1F70B5',
    monogram: 'NS',
    authMethod: 'token',
    popular: true,
    authFields: [
      {
        key: 'accountId',
        label: 'Account ID',
        type: 'text',
        placeholder: 'e.g. 3948571',
        required: true,
        demoValue: '3948571',
      },
      {
        key: 'consumerKey',
        label: 'Consumer Key',
        type: 'text',
        placeholder: 'TBA consumer key',
        required: true,
        demoValue: 'ck_9f81…',
      },
      {
        key: 'consumerSecret',
        label: 'Consumer Secret',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'cs_live_…',
      },
      {
        key: 'tokenId',
        label: 'Token ID',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'tkn_…',
      },
      {
        key: 'tokenSecret',
        label: 'Token Secret',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'tks_…',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'ns-products',
        sourceName: 'Item — Inventory Items',
        description:
          'NetSuite item records (inventory + non-inventory) with pricing.',
        rowCount: 3349051,
        columns: NETSUITE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('ns', {
        'purchase-orders': 'PurchaseOrder — Transactions',
        suppliers: 'Vendor — Entities',
        inventory: 'InventoryBalance — Locations',
        'spa-agreements': 'PriceLevel — Customer Pricing',
      }),
    ],
  },
  {
    id: 'dynamics-365',
    name: 'Microsoft Dynamics 365',
    category: 'erp',
    tagline: 'Finance & Supply Chain',
    description:
      'Sync released products, vendors, and purchasing from Dynamics 365 F&SCM.',
    brandColor: '#002050',
    monogram: 'D365',
    authMethod: 'oauth2',
    authFields: OAUTH_FIELDS(
      'Environment URL',
      'https://org.operations.dynamics.com',
      'https://gently-prod.operations.dynamics.com',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'd365-products',
        sourceName: 'EcoResProduct — Released Products',
        description:
          'Released products joined with InventTable cost and on-hand.',
        rowCount: 4100233,
        columns: DYNAMICS_PRODUCT_COLUMNS,
      }),
      ...commonLinks('d365', {
        'purchase-orders': 'PurchTable — Purchase Orders',
        suppliers: 'VendTable — Vendors',
        inventory: 'InventSum — Inventory Sums',
        rebates: 'PriceDiscTable — Trade Agreements',
      }),
    ],
  },
  {
    id: 'sap-ecc',
    name: 'SAP ECC',
    category: 'erp',
    tagline: 'On-Prem ERP (ECC 6.0)',
    description:
      'Connect a classic SAP ERP Central Component system to sync materials, procurement, and finance master data via the SAP Gateway.',
    brandColor: '#008FD3',
    monogram: 'ECC',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'Application Server',
        type: 'text',
        placeholder: 'e.g. sapecc.yourco.com',
        required: true,
        demoValue: 'sapecc.gently.com',
      },
      {
        key: 'systemNumber',
        label: 'System Number',
        type: 'text',
        placeholder: 'e.g. 00',
        required: true,
        demoValue: '00',
      },
      {
        key: 'client',
        label: 'Client (MANDT)',
        type: 'text',
        placeholder: 'e.g. 100',
        required: true,
        demoValue: '100',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. RFCUSER',
        required: true,
        demoValue: 'GENTLY_RFC',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'rfc_pass_100',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'ecc-products',
        sourceName: 'MARA — Material Master (ECC)',
        description:
          'ECC material master joined with MAKT short texts and MBEW valuation.',
        rowCount: 4421889,
        columns: SAP_PRODUCT_COLUMNS,
      }),
      ...commonLinks('ecc', {
        'purchase-orders': 'EKKO / EKPO — Purchasing Documents',
        suppliers: 'LFA1 — Vendor Master',
        inventory: 'MARD — Storage Location Stock',
        rebates: 'KONA — Rebate Agreements',
        'spa-agreements': 'A017 — Pricing Conditions',
      }),
    ],
  },
  {
    id: 'oracle-ebs',
    name: 'Oracle E-Business Suite',
    category: 'erp',
    tagline: 'Oracle EBS R12',
    description:
      'Connect Oracle E-Business Suite (R12) to sync the item master, suppliers, and procurement.',
    brandColor: '#B7472A',
    monogram: 'EBS',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'EBS Host',
        type: 'url',
        placeholder: 'https://ebs.yourco.com:8000',
        required: true,
        demoValue: 'https://ebs.gently.com:8000',
      },
      {
        key: 'responsibility',
        label: 'Responsibility',
        type: 'text',
        placeholder: 'e.g. Inventory, Vision Operations',
        required: true,
        demoValue: 'Inventory, Vision Operations',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. GENTLY_INT',
        required: true,
        demoValue: 'GENTLY_INT',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'ebs_pass_r12',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'ebs-products',
        sourceName: 'MTL_SYSTEM_ITEMS_B — Item Master',
        description:
          'Inventory item master with list price and on-hand from EBS R12.',
        rowCount: 5039221,
        columns: EBS_PRODUCT_COLUMNS,
      }),
      ...commonLinks('ebs', {
        'purchase-orders': 'PO_HEADERS_ALL — Purchase Orders',
        suppliers: 'AP_SUPPLIERS — Suppliers',
        inventory: 'MTL_ONHAND_QUANTITIES — On-Hand',
        'spa-agreements': 'QP_LIST_HEADERS — Price Lists',
      }),
    ],
  },
  {
    id: 'peoplesoft',
    name: 'Oracle PeopleSoft',
    category: 'erp',
    tagline: 'PeopleSoft FSCM',
    description:
      'Sync items, suppliers, and purchasing from Oracle PeopleSoft Financials & Supply Chain Management.',
    brandColor: '#C74634',
    monogram: 'PSFT',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'PIA / Gateway URL',
        type: 'url',
        placeholder: 'https://psoft.yourco.com/PSIGW/RESTListeningConnector',
        required: true,
        demoValue: 'https://psoft.gently.com/PSIGW/RESTListeningConnector',
      },
      {
        key: 'node',
        label: 'Integration Node',
        type: 'text',
        placeholder: 'e.g. GENTLY_NODE',
        required: true,
        demoValue: 'GENTLY_NODE',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. GENTLYINT',
        required: true,
        demoValue: 'GENTLYINT',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'ps_int_pass',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'psft-products',
        sourceName: 'PS_MASTER_ITEM_TBL — Item Master',
        description: 'PeopleSoft master item table with pricing and category.',
        rowCount: 2884511,
        columns: PEOPLESOFT_PRODUCT_COLUMNS,
      }),
      ...commonLinks('psft', {
        'purchase-orders': 'PS_PO_HDR / PS_PO_LINE — Purchase Orders',
        suppliers: 'PS_VENDOR — Vendor',
        inventory: 'PS_BU_ITEMS_INV — Business-Unit Items',
      }),
    ],
  },
  {
    id: 'infor',
    name: 'Infor (M3 / LN)',
    category: 'erp',
    tagline: 'Infor CloudSuite',
    description:
      'Connect Infor CloudSuite (M3 / LN) via the ION API to sync items, suppliers, and orders.',
    brandColor: '#E1251B',
    monogram: 'INF',
    authMethod: 'oauth2',
    authFields: [
      {
        key: 'ionUrl',
        label: 'ION API URL',
        type: 'url',
        placeholder: 'https://mingle-ionapi.inforcloudsuite.com',
        required: true,
        demoValue: 'https://mingle-ionapi.inforcloudsuite.com/GENTLY_PRD',
      },
      {
        key: 'clientId',
        label: 'Client ID',
        type: 'text',
        placeholder: 'e.g. GENTLY~aV2…',
        required: true,
        demoValue: 'GENTLY~aV2x9Qf',
      },
      {
        key: 'clientSecret',
        label: 'Client Secret',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'sk_infor_7Hd2…',
      },
      {
        key: 'tenant',
        label: 'Tenant',
        type: 'text',
        placeholder: 'e.g. GENTLY_PRD',
        required: true,
        demoValue: 'GENTLY_PRD',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'infor-products',
        sourceName: 'MITMAS — Item Master (M3)',
        description: 'M3 item master (MITMAS) exposed through the ION API.',
        rowCount: 3120884,
        columns: INFOR_PRODUCT_COLUMNS,
      }),
      ...commonLinks('infor', {
        'purchase-orders': 'MPHEAD / MPLINE — Purchase Orders',
        suppliers: 'CIDMAS — Supplier Master',
        inventory: 'MITBAL — Item Balance',
      }),
    ],
  },
  {
    id: 'epicor',
    name: 'Epicor Kinetic',
    category: 'erp',
    tagline: 'Manufacturing & Distribution ERP',
    description:
      'Connect Epicor Kinetic (ERP 10+) via REST to sync parts, suppliers, and purchase orders.',
    brandColor: '#E4002B',
    monogram: 'EPI',
    authMethod: 'api-key',
    authFields: [
      {
        key: 'host',
        label: 'Server URL',
        type: 'url',
        placeholder: 'https://epicor.yourco.com/server/api/v2',
        required: true,
        demoValue: 'https://epicor.gently.com/server/api/v2',
      },
      {
        key: 'company',
        label: 'Company',
        type: 'text',
        placeholder: 'e.g. GENTLY',
        required: true,
        demoValue: 'GENTLY',
      },
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'epi_live_8Kd3…',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. gently.svc',
        required: true,
        demoValue: 'gently.svc',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'epicor-products',
        sourceName: 'Erp.Part — Part Master',
        description: 'Epicor part master with pricing and warehouse on-hand.',
        rowCount: 1894220,
        columns: EPICOR_PRODUCT_COLUMNS,
      }),
      ...commonLinks('epicor', {
        'purchase-orders': 'Erp.POHeader — Purchase Orders',
        suppliers: 'Erp.Vendor — Vendors',
        inventory: 'Erp.PartWhse — Warehouse Quantities',
      }),
    ],
  },
  {
    id: 'syspro',
    name: 'SYSPRO',
    category: 'erp',
    tagline: 'Manufacturing & Distribution ERP',
    description:
      'Connect SYSPRO ERP via the e.net REST API to sync inventory, suppliers, and purchase orders.',
    brandColor: '#6AA539',
    monogram: 'SYS',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'e.net Endpoint',
        type: 'url',
        placeholder: 'https://syspro.yourco.com/SYSPROWCFService/Rest',
        required: true,
        demoValue: 'https://syspro.gently.com/SYSPROWCFService/Rest',
      },
      {
        key: 'company',
        label: 'Company ID',
        type: 'text',
        placeholder: 'e.g. E1',
        required: true,
        demoValue: 'E1',
      },
      {
        key: 'operator',
        label: 'Operator',
        type: 'text',
        placeholder: 'e.g. GENTLY',
        required: true,
        demoValue: 'GENTLY',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'syspro_pass',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'syspro-products',
        sourceName: 'InvMaster — Stock Master',
        description:
          'SYSPRO stock master with selling price and quantity on hand.',
        rowCount: 987441,
        columns: SYSPRO_PRODUCT_COLUMNS,
      }),
      ...commonLinks('syspro', {
        'purchase-orders': 'PorMasterHdr — Purchase Orders',
        suppliers: 'ApSupplier — Suppliers',
        inventory: 'InvWarehouse — Warehouse Stock',
      }),
    ],
  },
  {
    id: 'workday',
    name: 'Workday',
    category: 'erp',
    tagline: 'Financials & Procurement',
    description:
      'Connect Workday Financial Management to sync procurement items, suppliers, and purchase orders via the Workday API.',
    brandColor: '#0875E1',
    monogram: 'WD',
    authMethod: 'oauth2',
    authFields: OAUTH_FIELDS(
      'Workday Host',
      'https://wd5-impl-services1.workday.com/ccx/service/tenant',
      'https://wd5-services1.workday.com/ccx/service/gently',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'wd-products',
        sourceName: 'Purchase_Item — Procurement (RaaS)',
        description:
          'Workday procurement purchase items exposed via a RaaS report.',
        rowCount: 742118,
        columns: WORKDAY_PRODUCT_COLUMNS,
      }),
      ...commonLinks('wd', {
        'purchase-orders': 'Purchase_Order — Procurement',
        suppliers: 'Supplier — Procurement',
      }),
    ],
  },
  {
    id: 'dynamics-nav',
    name: 'Microsoft Dynamics NAV (Navision)',
    category: 'erp',
    tagline: 'Dynamics NAV / Navision',
    description:
      'Connect Microsoft Dynamics NAV (Navision) via OData web services to sync items, vendors, and purchasing.',
    brandColor: '#0E6CBD',
    monogram: 'NAV',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'OData Service URL',
        type: 'url',
        placeholder: 'https://nav.yourco.com:7048/NAV/OData',
        required: true,
        demoValue: 'https://nav.gently.com:7048/DynamicsNAV/OData',
      },
      {
        key: 'company',
        label: 'Company',
        type: 'text',
        placeholder: 'e.g. CRONUS',
        required: true,
        demoValue: 'Gently Distribution',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. GENTLY\\svc',
        required: true,
        demoValue: 'GENTLY\\navsvc',
      },
      {
        key: 'password',
        label: 'Web Service Access Key',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'nav_ws_key_8fA2',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'nav-products',
        sourceName: 'Item — Item Card (NAV)',
        description: 'Dynamics NAV item card exposed as an OData web service.',
        rowCount: 1264880,
        columns: NAV_PRODUCT_COLUMNS,
      }),
      ...commonLinks('nav', {
        'purchase-orders': 'Purchase_Header / Purchase_Line — Purchase Orders',
        suppliers: 'Vendor — Vendor Card',
        inventory: 'Item_Ledger_Entry — Inventory',
      }),
    ],
  },
  {
    id: 'jd-edwards',
    name: 'Oracle JD Edwards EnterpriseOne',
    category: 'erp',
    tagline: 'JD Edwards E1',
    description:
      'Connect Oracle JD Edwards EnterpriseOne via AIS REST services to sync the item master, suppliers, and procurement.',
    brandColor: '#C74634',
    monogram: 'JDE',
    authMethod: 'basic',
    authFields: [
      {
        key: 'host',
        label: 'AIS Server URL',
        type: 'url',
        placeholder: 'https://jde.yourco.com/jderest',
        required: true,
        demoValue: 'https://jde.gently.com/jderest',
      },
      {
        key: 'environment',
        label: 'Environment',
        type: 'text',
        placeholder: 'e.g. JPD920',
        required: true,
        demoValue: 'JPD920',
      },
      {
        key: 'role',
        label: 'Role',
        type: 'text',
        placeholder: 'e.g. *ALL',
        required: true,
        demoValue: '*ALL',
      },
      {
        key: 'username',
        label: 'Username',
        type: 'text',
        placeholder: 'e.g. GENTLYINT',
        required: true,
        demoValue: 'GENTLYINT',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'jde_pass_e1',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'jde-products',
        sourceName: 'F4101 — Item Master',
        description:
          'JD Edwards item master (F4101) joined with F4105 cost and F41021 on-hand.',
        rowCount: 3578120,
        columns: JDE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('jde', {
        'purchase-orders': 'F4301 / F4311 — Purchase Orders',
        suppliers: 'F0401 — Supplier Master',
        inventory: 'F41021 — Item Location',
      }),
    ],
  },
  {
    id: 'as400',
    name: 'IBM i (AS/400)',
    category: 'data-platform',
    tagline: 'DB2 for i',
    description:
      'Connect directly to DB2 for i (AS/400) physical files for legacy item master and procurement data.',
    brandColor: '#1F70C1',
    monogram: 'IBM',
    authMethod: 'basic',
    popular: true,
    authFields: [
      {
        key: 'host',
        label: 'System (IP / Host)',
        type: 'text',
        placeholder: 'e.g. 10.20.0.14 or PUB400',
        required: true,
        demoValue: '10.20.0.14',
      },
      {
        key: 'library',
        label: 'Library',
        type: 'text',
        placeholder: 'e.g. RXLDATA',
        required: true,
        demoValue: 'RXLDATA',
      },
      {
        key: 'username',
        label: 'User Profile',
        type: 'text',
        placeholder: 'e.g. GENTLYSVC',
        required: true,
        demoValue: 'GENTLYSVC',
      },
      {
        key: 'password',
        label: 'Password',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'svc_pass_4400',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'as400-products',
        sourceName: 'ITEMMAST5 — Item Master Physical File',
        description:
          'INITINFC item master physical file (gi* fields) keyed by item + location.',
        rowCount: 6128744,
        columns: AS400_PRODUCT_COLUMNS,
        // Baseline source of record: the core catalog, no discrepancies.
        products: CORE_PRODUCTS,
      }),
      ...commonLinks('as400', {
        'purchase-orders': 'POMAST — Purchase Order Master',
        suppliers: 'VENDMAST — Vendor Master',
        inventory: 'INVBAL — Inventory Balances',
        rebates: 'REBATEMST — Rebate Master',
        'spa-agreements': 'CONTRACTMST — Contract Master',
      }),
    ],
  },
  // ---- Data platforms ----
  {
    id: 'snowflake',
    name: 'Snowflake',
    category: 'data-platform',
    tagline: 'Cloud Data Cloud',
    description:
      'Read curated ERP marts directly from your Snowflake account with key-pair auth.',
    brandColor: '#29B5E8',
    monogram: '❄',
    authMethod: 'service-account',
    popular: true,
    authFields: [
      {
        key: 'account',
        label: 'Account Identifier',
        type: 'text',
        placeholder: 'orgname-account',
        required: true,
        demoValue: 'gently-prod.us-east-1',
      },
      {
        key: 'warehouse',
        label: 'Warehouse',
        type: 'text',
        placeholder: 'e.g. INGEST_WH',
        required: true,
        demoValue: 'INGEST_WH',
      },
      {
        key: 'database',
        label: 'Database',
        type: 'text',
        placeholder: 'e.g. ANALYTICS',
        required: true,
        demoValue: 'ANALYTICS',
      },
      {
        key: 'role',
        label: 'Role',
        type: 'text',
        placeholder: 'e.g. GENTLY_READER',
        required: true,
        demoValue: 'GENTLY_READER',
      },
      {
        key: 'privateKey',
        label: 'Private Key (PEM)',
        type: 'password',
        placeholder: '-----BEGIN PRIVATE KEY-----',
        required: true,
        demoValue: '-----BEGIN PRIVATE KEY-----\nMIIEvQ…',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'snow-products',
        sourceName: 'ANALYTICS.ERP.DIM_PRODUCT',
        description: 'Curated product dimension built from the ERP gold layer.',
        rowCount: 5231004,
        columns: WAREHOUSE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('snow', {
        'purchase-orders': 'ANALYTICS.ERP.FACT_PURCHASE_ORDER',
        suppliers: 'ANALYTICS.ERP.DIM_SUPPLIER',
        inventory: 'ANALYTICS.ERP.FACT_INVENTORY',
        rebates: 'ANALYTICS.ERP.FACT_REBATE_CLAIM',
        'spa-agreements': 'ANALYTICS.ERP.DIM_SPA_AGREEMENT',
      }),
    ],
  },
  {
    id: 'databricks',
    name: 'Databricks Lakehouse',
    category: 'data-platform',
    tagline: 'Unity Catalog',
    description:
      'Query Delta tables in your Databricks lakehouse through a SQL warehouse endpoint.',
    brandColor: '#FF3621',
    monogram: 'DBX',
    authMethod: 'token',
    authFields: [
      {
        key: 'host',
        label: 'Workspace Host',
        type: 'url',
        placeholder: 'https://dbc-xxxx.cloud.databricks.com',
        required: true,
        demoValue: 'https://dbc-1a2b3c.cloud.databricks.com',
      },
      {
        key: 'httpPath',
        label: 'SQL Warehouse HTTP Path',
        type: 'text',
        placeholder: '/sql/1.0/warehouses/abc123',
        required: true,
        demoValue: '/sql/1.0/warehouses/9f3c1a2b',
      },
      {
        key: 'token',
        label: 'Personal Access Token',
        type: 'password',
        placeholder: 'dapi••••••••',
        required: true,
        demoValue: 'dapi8f3c…',
      },
      {
        key: 'catalog',
        label: 'Catalog',
        type: 'text',
        placeholder: 'e.g. erp_silver',
        required: true,
        demoValue: 'erp_silver',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'dbx-products',
        sourceName: 'erp_silver.products',
        description: 'Silver-layer Delta table of conformed product records.',
        rowCount: 4998120,
        columns: WAREHOUSE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('dbx', {
        'purchase-orders': 'erp_silver.purchase_orders',
        suppliers: 'erp_silver.suppliers',
        inventory: 'erp_silver.inventory',
        rebates: 'erp_silver.rebate_claims',
      }),
    ],
  },
  {
    id: 'ms-fabric',
    name: 'Microsoft Fabric',
    category: 'data-platform',
    tagline: 'OneLake Lakehouse',
    description:
      'Connect a Fabric Lakehouse / Warehouse and read ERP tables from OneLake.',
    brandColor: '#11A693',
    monogram: 'FAB',
    authMethod: 'oauth2',
    authFields: OAUTH_FIELDS(
      'SQL Endpoint',
      'xxxx.datawarehouse.fabric.microsoft.com',
      'g3nt-ly.datawarehouse.fabric.microsoft.com',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'fab-products',
        sourceName: 'Lakehouse.dbo.Products',
        description:
          'Fabric Lakehouse product table materialized from OneLake.',
        rowCount: 5012447,
        columns: WAREHOUSE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('fab', {
        'purchase-orders': 'Lakehouse.dbo.PurchaseOrders',
        suppliers: 'Lakehouse.dbo.Suppliers',
        inventory: 'Lakehouse.dbo.Inventory',
      }),
    ],
  },
  {
    id: 'sap-datasphere',
    name: 'SAP Datasphere',
    category: 'data-platform',
    tagline: 'Business Data Fabric',
    description:
      'Consume analytical and relational views modeled in SAP Datasphere spaces.',
    brandColor: '#008FD3',
    monogram: 'DSP',
    authMethod: 'oauth2',
    authFields: OAUTH_FIELDS(
      'Datasphere Host',
      'xxxx.us10.hcs.cloud.sap',
      'gently.us10.hcs.cloud.sap',
    ),
    detectedLinks: [
      buildProductsLink({
        id: 'dsp-products',
        sourceName: 'ERP.V_PRODUCT — Analytical View',
        description:
          'Datasphere relational view exposing conformed product master.',
        rowCount: 4899003,
        columns: WAREHOUSE_PRODUCT_COLUMNS,
      }),
      ...commonLinks('dsp', {
        'purchase-orders': 'ERP.V_PURCHASE_ORDER',
        suppliers: 'ERP.V_SUPPLIER',
        rebates: 'ERP.V_REBATE',
      }),
    ],
  },
  // ---- Specialized ----
  {
    id: 'famous',
    name: 'FAMOUS',
    category: 'specialized',
    tagline: 'Distribution ERP',
    description:
      'Connect FAMOUS distribution software for product, inventory, and AP/AR master data.',
    brandColor: '#2E7D32',
    monogram: 'FAM',
    authMethod: 'api-key',
    authFields: [
      {
        key: 'host',
        label: 'FAMOUS Server',
        type: 'text',
        placeholder: 'e.g. famous.yourco.com',
        required: true,
        demoValue: 'famous.gently.com',
      },
      {
        key: 'company',
        label: 'Company Code',
        type: 'text',
        placeholder: 'e.g. 01',
        required: true,
        demoValue: '01',
      },
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'fk_live_3kZ9…',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'fam-products',
        sourceName: 'PRODUCT_MASTER',
        description: 'FAMOUS product master with pricing and availability.',
        rowCount: 2284118,
        columns: GENERIC_PRODUCT_COLUMNS,
      }),
      ...commonLinks('fam', {
        'purchase-orders': 'PO_HEADER',
        suppliers: 'VENDOR_MASTER',
        inventory: 'INVENTORY',
        rebates: 'REBATE_DEALS',
        'spa-agreements': 'PRICE_CONTRACTS',
      }),
    ],
  },
  {
    id: 'gainsystems',
    name: 'GAINSystems',
    category: 'specialized',
    tagline: 'Inventory & Supply Planning',
    description:
      'Sync demand, replenishment plans, and item planning parameters from GAINSystems.',
    brandColor: '#00529B',
    monogram: 'GS',
    authMethod: 'api-key',
    authFields: [
      {
        key: 'host',
        label: 'GAINS Host',
        type: 'url',
        placeholder: 'https://gains.yourco.com',
        required: true,
        demoValue: 'https://gains.gently.com',
      },
      {
        key: 'tenant',
        label: 'Tenant',
        type: 'text',
        placeholder: 'e.g. GENTLY',
        required: true,
        demoValue: 'GENTLY',
      },
      {
        key: 'apiKey',
        label: 'API Key',
        type: 'password',
        placeholder: '••••••••',
        required: true,
        demoValue: 'gns_live_77aF…',
      },
    ],
    detectedLinks: [
      buildProductsLink({
        id: 'gs-products',
        sourceName: 'ITEM_PLANNING',
        description:
          'GAINS item planning master with lead time and policy fields.',
        rowCount: 6120447,
        columns: GENERIC_PRODUCT_COLUMNS,
      }),
      ...commonLinks('gs', {
        'purchase-orders': 'PURCHASE_PLAN',
        suppliers: 'SUPPLIER',
        inventory: 'STOCK_POSITION',
      }),
    ],
  },
];

export function getIntegration(id: string): IntegrationDef | undefined {
  return INTEGRATIONS.find((i) => i.id === id);
}

export const CATEGORY_LABELS: Record<string, string> = {
  erp: 'ERP Systems',
  'data-platform': 'Data Platforms',
  specialized: 'Specialized Solutions',
};
